import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import CorrectNameModal from './CorrectNameModal'
import ClearFlagModal from './ClearFlagModal'
import ReviewShipperIdModal from './ReviewShipperIdModal'
import { IntelligentCheckList, IntelligentCheckBadge } from '../kyc/IntelligentCheck'
import {
  FLAG_LABELS,
  ID_TYPE_LABELS,
  SHIPPER_REVIEW_META,
  ShipperReviewStatus,
  canViewShipperIds,
  initials,
} from './shared'
import '../kyc/KycReview.scss'
import '../kyc/KycDetail.scss'
import './ShipperIds.scss'

const ShipperIdDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, notify } = useGlobalContext()

  const [identity, setIdentity] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [activeVersion, setActiveVersion] = useState<number | null>(null)
  const [showNumber, setShowNumber] = useState(false)
  const [lightbox, setLightbox] = useState(false)
  const [showCorrectName, setShowCorrectName] = useState(false)
  const [flagToClear, setFlagToClear] = useState<string | null>(null)
  const [decision, setDecision] = useState<'approved' | 'rejected' | null>(null)

  const canView = canViewShipperIds(profile)

  // Every load is audit-logged server-side (it returns the full ID number),
  // so this only runs on open and on an explicit reload.
  const fetchDetail = () => {
    setLoading(true)
    get(`admin/customer-identities/${id}`)
      .then((res: any) => {
        if (res.success) {
          setIdentity(res.data)
          setActiveVersion(res.data.currentVersion)
        } else if (res.status !== 404) notify('error', res.message)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (canView) fetchDetail()
    setShowNumber(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (!canView) {
    return (
      <div className='kyc-detail shipper-id-detail'>
        <div className='restricted-card'>
          <Icon icon='mdi:shield-lock' />
          <h1>Restricted</h1>
          <p>Shipper IDs are only available to super admins and customer service.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className='kyc-detail shipper-id-detail'>
        <FormLoader position='center' />
      </div>
    )
  }

  if (!identity) {
    return (
      <div className='kyc-detail shipper-id-detail'>
        <div className='restricted-card'>
          <Icon icon='mdi:file-question-outline' />
          <h1>Not found</h1>
          <p>This shipper ID does not exist.</p>
        </div>
      </div>
    )
  }

  const reviewStatus: ShipperReviewStatus = identity.status || 'pending'
  const reviewMeta = SHIPPER_REVIEW_META[reviewStatus] || SHIPPER_REVIEW_META.pending
  const versions: any[] = identity.versions || []
  const shown =
    versions.find((v) => v.version === activeVersion) || versions[0] || null

  return (
    <div className='kyc-detail shipper-id-detail'>
      {showCorrectName && (
        <CorrectNameModal
          identityId={id!}
          currentName={identity.fullName}
          onClose={() => setShowCorrectName(false)}
          onSuccess={fetchDetail}
        />
      )}

      {decision && (
        <ReviewShipperIdModal
          identityId={id!}
          shipperName={identity.fullName}
          version={identity.currentVersion}
          decision={decision}
          onClose={() => setDecision(null)}
          onSuccess={fetchDetail}
        />
      )}

      {flagToClear && (
        <ClearFlagModal
          identityId={id!}
          code={flagToClear}
          onClose={() => setFlagToClear(null)}
          onSuccess={fetchDetail}
        />
      )}

      {lightbox && shown?.imageUrl && (
        <div className='kyc-lightbox' onClick={() => setLightbox(false)}>
          <button className='close'>
            <Icon icon='mdi:close' />
          </button>
          <img
            src={shown.imageUrl}
            alt={`ID version ${shown.version}`}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className='top flex justify-between items-center flex-wrap'>
        <div
          className='back-btn flex items-center'
          onClick={() => navigate('/admin/shipper-ids')}
        >
          <Icon icon='ion:return-up-back-outline' />
          <p>Back to shipper IDs</p>
        </div>
        {identity.flags?.length > 0 && (
          <div className='top-right flex items-center'>
            <span className='flag-tag'>
              <Icon icon='mdi:flag' /> Flagged
            </span>
          </div>
        )}
      </div>

      <div className='detail-grid'>
        {/* ── ID image, per version ─────────────────── */}
        <div className='documents-panel'>
          <div className='doc-tabs'>
            {versions.map((v) => (
              <button
                key={v.version}
                className={shown?.version === v.version ? 'active' : ''}
                onClick={() => setActiveVersion(v.version)}
              >
                <Icon
                  icon={
                    v.correctedBy
                      ? 'mdi:account-edit-outline'
                      : 'mdi:card-account-details-outline'
                  }
                />
                <span>
                  v{v.version}
                  {v.version === identity.currentVersion ? ' · current' : ''}
                  {v.review?.status === 'rejected' ? ' · rejected' : ''}
                </span>
              </button>
            ))}
          </div>

          {shown && (
            <div className='doc-meta flex justify-between items-center flex-wrap'>
              <p className='doc-type'>
                {shown.fullName} · {new Date(shown.createdAt).toLocaleString()}
              </p>
              <IntelligentCheckBadge review={shown.intelligentCheck} />
              {shown.correctedBy && (
                <span className='age-tag good'>
                  <Icon icon='mdi:account-edit-outline' />
                  Name corrected by staff
                  {shown.correctionReason ? `: ${shown.correctionReason}` : ''}
                </span>
              )}
            </div>
          )}

          <div className='doc-stage'>
            {!shown?.imageUrl ? (
              <div className='doc-missing'>
                <Icon icon='mdi:file-alert-outline' />
                <p>Image link expired.</p>
                <button onClick={fetchDetail}>Reload image</button>
              </div>
            ) : (
              <div className='doc-image' onClick={() => setLightbox(true)}>
                <img src={shown.imageUrl} alt={`ID version ${shown.version}`} />
                <div className='zoom-hint'>
                  <Icon icon='mdi:magnify-plus-outline' /> Click to zoom
                </div>
              </div>
            )}
          </div>
          <p className='signed-note'>
            <Icon icon='mdi:lock-outline' /> Secure links expire after 10 minutes —
            reload the page to regenerate them. Every view of this page is logged.
          </p>

          {shown && <IntelligentCheckList review={shown.intelligentCheck} image='idImage' />}
        </div>

        {/* ── Shipper, flags, bookings ──────────────── */}
        <div className='side-panel'>
          <div className='customer-card'>
            <div className='card-header'>
              <div className='avatar'>{initials(identity.fullName)}</div>
              <div>
                <h2>{identity.fullName}</h2>
                <p>{ID_TYPE_LABELS[identity.idType] || identity.idType}</p>
              </div>
              <button
                className='profile-link'
                title='Correct the name on this ID'
                onClick={() => setShowCorrectName(true)}
              >
                <Icon icon='mdi:pencil-outline' />
              </button>
            </div>

            <div className='fields'>
              <div className='field nin-field'>
                <p>ID number</p>
                <div className='flex items-center'>
                  <h4 className='mono'>
                    {showNumber ? identity.idNumberFull : identity.idNumber}
                  </h4>
                  <button
                    onClick={() => setShowNumber(!showNumber)}
                    title={showNumber ? 'Hide' : 'Reveal'}
                  >
                    <Icon icon={showNumber ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} />
                  </button>
                </div>
              </div>
              <div className='field'>
                <p>Registered by</p>
                <h4>
                  {identity.business ? (
                    <span
                      className='link'
                      onClick={() =>
                        navigate(`/admin/business/${identity.business.businessId}`)
                      }
                    >
                      {identity.business.org_name}
                    </span>
                  ) : (
                    '—'
                  )}
                </h4>
              </div>
              {identity.externalCustomerId && (
                <div className='field'>
                  <p>Business's own ref</p>
                  <h4 className='mono'>{identity.externalCustomerId}</h4>
                </div>
              )}
              <div className='field'>
                <p>First registered</p>
                <h4>{new Date(identity.createdAt).toLocaleString()}</h4>
              </div>
              <div className='field'>
                <p>Current version</p>
                <h4>v{identity.currentVersion}</h4>
              </div>
            </div>
          </div>

          <div className='decision-card'>
            <h3>Review</h3>
            <div className='fields'>
              <div className='field'>
                <p>Status</p>
                <h4>
                  <span className={`kyc-pill ${reviewMeta.className}`}>
                    {reviewMeta.label}
                  </span>
                </h4>
              </div>
              {identity.review?.reviewedAt && (
                <div className='field'>
                  <p>Reviewed by</p>
                  <h4>
                    {identity.review.reviewedByName || 'Staff'} · v
                    {identity.review.version} ·{' '}
                    {new Date(identity.review.reviewedAt).toLocaleString()}
                  </h4>
                </div>
              )}
              {reviewStatus === 'rejected' && identity.review?.reason && (
                <div className='field'>
                  <p>Rejection reason</p>
                  <h4 className='reason'>{identity.review.reason}</h4>
                </div>
              )}
            </div>
            <p className='muted'>
              {reviewStatus === 'rejected'
                ? 'Bookings with this ID are refused until the business uploads a new photo.'
                : 'Pending and approved IDs can both be booked with. Rejecting stops bookings until the business uploads a new photo.'}
            </p>
            <div className='actions'>
              {reviewStatus !== 'rejected' && (
                <button className='btn-reject' onClick={() => setDecision('rejected')}>
                  <Icon icon='mdi:close-circle-outline' /> Reject
                </button>
              )}
              {reviewStatus !== 'approved' && (
                <button className='btn-approve' onClick={() => setDecision('approved')}>
                  <Icon icon='mdi:shield-check-outline' /> Approve
                </button>
              )}
            </div>
          </div>

          {(identity.flags?.length > 0 || identity.sameIdElsewhere?.length > 0) && (
            <div className='submission-card'>
              <h3>Flags</h3>
              {identity.flags?.map((flag: any) => (
                <div className='flag-row flex justify-between items-center' key={flag.code}>
                  <div>
                    <p className='flag-label'>
                      <Icon icon='mdi:flag' /> {FLAG_LABELS[flag.code] || flag.code}
                    </p>
                    <p className='muted'>
                      Raised {new Date(flag.at).toLocaleDateString()}
                    </p>
                  </div>
                  <button className='small-btn' onClick={() => setFlagToClear(flag.code)}>
                    Clear
                  </button>
                </div>
              ))}
              {identity.sameIdElsewhere?.length > 0 && (
                <div className='elsewhere'>
                  <p className='muted'>Same ID number registered by:</p>
                  {identity.sameIdElsewhere.map((other: any) => (
                    <div
                      key={other.customerIdentityId}
                      className='elsewhere-row flex justify-between items-center'
                      onClick={() =>
                        navigate(`/admin/shipper-id/${other.customerIdentityId}`)
                      }
                    >
                      <div>
                        <p>{other.business?.org_name || 'Unknown business'}</p>
                        <p className='muted'>as {other.fullName}</p>
                      </div>
                      <Icon icon='mdi:chevron-right' />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className='submission-card'>
            <h3>Bookings ({identity.bookings?.totalCount ?? 0})</h3>
            {identity.bookings?.recent?.length ? (
              <>
                {identity.bookings.recent.map((booking: any) => (
                  <div
                    key={booking.bookingId}
                    className='booking-row flex justify-between items-center'
                    onClick={() => navigate(`/admin/booking/${booking.bookingId}`)}
                  >
                    <div>
                      <p className='mono'>{booking.trackingId || booking.bookingId}</p>
                      <p className='muted'>
                        {booking.booking_status?.description || '—'} ·{' '}
                        {new Date(booking.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span className='version-tag'>v{booking.identityVersion}</span>
                  </div>
                ))}
                {identity.bookings.totalCount > identity.bookings.recent.length && (
                  <p className='muted more-note'>
                    Showing the latest {identity.bookings.recent.length}.
                  </p>
                )}
              </>
            ) : (
              <p className='muted'>No bookings made with this ID yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShipperIdDetail
